import { mapPolygonPoints } from "@constructionautomation/backend/convex/lib/measurementGeometry";
import type {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  MouseEvent as ReactMouseEvent,
} from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { Point } from "@/lib/geometry";
import { TOOL_IDS } from "@/viewport-tools/measurement";

import { normalizeRect, translateGeometry } from "../measurement-overlay-helpers";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  type DraftMeasurementState,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type PolygonGeometry,
} from "../measurement-overlay-types";
import type {
  InteractionState,
  LabelResizeHandle,
  MeasurementOverlayContextMenuState,
  ResizeHandle,
} from "./types";

type PreviewGeometryMap = Record<string, MeasurementGeometry | PolygonGeometry>;

/** Floor for label card width (~32 CSS px converted into page units). */
export const MIN_LABEL_TEXT_BOX_WIDTH_SCREEN_PX = 32;

export function screenLengthToPageWidth(
  screenPx: number,
  pageToScreenPoint: (point: Point) => Point
): number {
  const origin = pageToScreenPoint({ x: 0, y: 0 });
  const unit = pageToScreenPoint({ x: 1, y: 0 });
  const screenPerPageUnit = Math.abs(unit.x - origin.x);
  if (screenPerPageUnit < 1e-9) {
    return screenPx;
  }
  return screenPx / screenPerPageUnit;
}

export function pageWidthToScreenLength(
  pageWidth: number,
  pageToScreenPoint: (point: Point) => Point
): number {
  const origin = pageToScreenPoint({ x: 0, y: 0 });
  const unit = pageToScreenPoint({ x: 1, y: 0 });
  return Math.abs(unit.x - origin.x) * pageWidth;
}

interface StartDraftMoveInteractionArgs {
  event: ReactMouseEvent;
  draft: DraftMeasurementState;
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
  setContextMenu: Dispatch<SetStateAction<MeasurementOverlayContextMenuState | null>>;
}

export function startDraftMoveInteraction({
  event,
  draft,
  drawingDraftRef,
  getPagePoint,
  interactionRef,
  setInteraction,
  setContextMenu,
}: StartDraftMoveInteractionArgs) {
  if (drawingDraftRef.current) {
    return;
  }

  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  const nextInteraction: InteractionState = {
    mode: "move-draft",
    startPoint: pagePoint,
    initialDraft: draft,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
  setContextMenu(null);
}

interface OpenMeasurementContextMenuInteractionArgs {
  event: ReactMouseEvent;
  measurement: MeasurementOverlayRecord;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  overlayRef: MutableRefObject<HTMLDivElement | null>;
  lastPointerPagePointRef: MutableRefObject<Point | null>;
  handleCancelDraft: () => void;
  onSelectMeasurement: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  setContextMenu: Dispatch<SetStateAction<MeasurementOverlayContextMenuState | null>>;
}

export function openMeasurementContextMenuInteraction({
  event,
  measurement,
  getPagePoint,
  overlayRef,
  lastPointerPagePointRef,
  handleCancelDraft,
  onSelectMeasurement,
  setContextMenu,
}: OpenMeasurementContextMenuInteractionArgs) {
  event.preventDefault();
  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  const overlayRect = overlayRef.current?.getBoundingClientRect();
  if (pagePoint) {
    lastPointerPagePointRef.current = pagePoint;
  }
  handleCancelDraft();
  onSelectMeasurement(measurement._id);
  setContextMenu({
    target: {
      type: "measurement",
      measurementId: measurement._id,
    },
    x: overlayRect ? event.clientX - overlayRect.left : event.clientX,
    y: overlayRect ? event.clientY - overlayRect.top : event.clientY,
    pagePoint,
  });
}

interface StartMeasurementMoveInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  geometry: MeasurementGeometry | PolygonGeometry;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  handleCancelDraft: () => void;
  measurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    options?: { shiftKey?: boolean }
  ) => void;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

export function startMeasurementMoveInteraction({
  event,
  measurementId,
  geometry,
  getPagePoint,
  handleCancelDraft,
  measurements,
  selectedMeasurementIds,
  onSelectMeasurement,
  setPreviewGeometries,
  interactionRef,
  setInteraction,
}: StartMeasurementMoveInteractionArgs) {
  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  handleCancelDraft();
  const isMovingSelection =
    selectedMeasurementIds.length > 1 &&
    selectedMeasurementIds.includes(measurementId);
  if (event.shiftKey || event.ctrlKey || event.metaKey) {
    onSelectMeasurement(measurementId, { shiftKey: true });
    return;
  }

  if (isMovingSelection) {
    const selectedSet = new Set(selectedMeasurementIds.map((id) => String(id)));
    const initialGeometries = measurements
      .filter((measurement) => selectedSet.has(String(measurement._id)))
      .map((measurement) => ({
        measurementId: measurement._id,
        geometry: measurement.geometry,
      }));

    setPreviewGeometries(
      Object.fromEntries(
        initialGeometries.map(({ measurementId: selectedId, geometry: selectedGeometry }) => [
          String(selectedId),
          selectedGeometry,
        ])
      )
    );
    const nextInteraction: InteractionState = {
      mode: "move-selection",
      startPoint: pagePoint,
      initialGeometries,
    };
    interactionRef.current = nextInteraction;
    setInteraction(nextInteraction);
    return;
  }

  onSelectMeasurement(measurementId);
  setPreviewGeometries({ [String(measurementId)]: geometry });
  const nextInteraction: InteractionState = {
    mode: "move",
    measurementId,
    startPoint: pagePoint,
    initialGeometry: geometry,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

interface StartMeasurementResizeInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  handle: ResizeHandle;
  geometry: MeasurementGeometry;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

export function startMeasurementResizeInteraction({
  event,
  measurementId,
  handle,
  geometry,
  getPagePoint,
  setPreviewGeometries,
  interactionRef,
  setInteraction,
}: StartMeasurementResizeInteractionArgs) {
  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  setPreviewGeometries({ [String(measurementId)]: geometry });
  const nextInteraction: InteractionState = {
    mode: "resize",
    measurementId,
    handle,
    startPoint: pagePoint,
    initialGeometry: geometry,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

interface StartMeasurementVertexDragInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  vertexIndex: number;
  geometry: PolygonGeometry;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

interface StartMeasurementLabelMoveInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  annotation: MeasurementAnnotation | undefined;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  handleCancelDraft: () => void;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    options?: { shiftKey?: boolean }
  ) => void;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

export function startMeasurementVertexDragInteraction({
  event,
  measurementId,
  vertexIndex,
  geometry,
  setPreviewGeometries,
  interactionRef,
  setInteraction,
}: StartMeasurementVertexDragInteractionArgs) {
  event.stopPropagation();
  setPreviewGeometries({ [String(measurementId)]: geometry });
  const nextInteraction: InteractionState = {
    mode: "vertex",
    measurementId,
    vertexIndex,
    initialGeometry: geometry,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

export function startMeasurementLabelMoveInteraction({
  event,
  measurementId,
  annotation,
  getPagePoint,
  handleCancelDraft,
  onSelectMeasurement,
  interactionRef,
  setInteraction,
}: StartMeasurementLabelMoveInteractionArgs) {
  event.stopPropagation();
  handleCancelDraft();

  if (event.shiftKey || event.ctrlKey || event.metaKey) {
    onSelectMeasurement(measurementId, { shiftKey: true });
    return;
  }

  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  onSelectMeasurement(measurementId);
  const nextInteraction: InteractionState = {
    mode: "move-label",
    measurementId,
    startPoint: pagePoint,
    initialAnnotation: annotation,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

interface StartMeasurementLabelResizeInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  handle: LabelResizeHandle;
  annotation: MeasurementAnnotation | undefined;
  initialWidth: number;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  handleCancelDraft: () => void;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    options?: { shiftKey?: boolean }
  ) => void;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

export function startMeasurementLabelResizeInteraction({
  event,
  measurementId,
  handle,
  annotation,
  initialWidth,
  getPagePoint,
  handleCancelDraft,
  onSelectMeasurement,
  interactionRef,
  setInteraction,
}: StartMeasurementLabelResizeInteractionArgs) {
  event.preventDefault();
  event.stopPropagation();
  handleCancelDraft();

  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  onSelectMeasurement(measurementId);
  const nextInteraction: InteractionState = {
    mode: "resize-label",
    measurementId,
    handle,
    startPoint: pagePoint,
    initialAnnotation: annotation,
    initialWidth,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

interface ApplyTransformPreviewOnPointerMoveArgs {
  activeInteraction: InteractionState;
  event: ReactMouseEvent<HTMLDivElement>;
  pagePoint: Point;
  viewport: DocumentViewportValue;
  measurements: MeasurementOverlayRecord[];
  snapEnabled: boolean;
  updateSnapHoverPoint: (point: Point) => Point | null;
  snapSegmentPoint: (
    anchor: Point,
    candidate: Point,
    options?: { allowPdfSnap?: boolean }
  ) => Point;
  snapRectangleCorner: (
    anchor: Point,
    candidate: Point,
    lockSquare: boolean,
    options?: { allowPdfSnap?: boolean }
  ) => Point;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  setPreviewAnnotations: Dispatch<SetStateAction<Record<string, MeasurementAnnotation>>>;
  updateUnsavedDraft: (nextDraft: DraftMeasurementState) => void;
}

// This dispatcher intentionally handles the distinct pan, move, vertex, and resize interaction modes.
// eslint-disable-next-line complexity
export function applyTransformPreviewOnPointerMove({
  activeInteraction,
  event,
  pagePoint,
  viewport,
  measurements,
  snapEnabled,
  updateSnapHoverPoint,
  snapSegmentPoint,
  snapRectangleCorner,
  setPreviewGeometries,
  setPreviewAnnotations,
  updateUnsavedDraft,
}: ApplyTransformPreviewOnPointerMoveArgs): boolean {
  const allowPdfSnap = snapEnabled && !event.shiftKey;

  if (activeInteraction.mode === "pan") {
    viewport.setPan({
      x: activeInteraction.initialPanX + (event.clientX - activeInteraction.startClientX),
      y: activeInteraction.initialPanY + (event.clientY - activeInteraction.startClientY),
    });
    return true;
  }

  if (activeInteraction.mode === "move") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    setPreviewGeometries({
      [String(activeInteraction.measurementId)]: translateGeometry(
        activeInteraction.initialGeometry,
        dx,
        dy
      ),
    });
    return true;
  }

  if (activeInteraction.mode === "move-selection") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    setPreviewGeometries(
      Object.fromEntries(
        activeInteraction.initialGeometries.map(({ measurementId, geometry }) => [
          String(measurementId),
          translateGeometry(geometry, dx, dy),
        ])
      )
    );
    return true;
  }

  if (activeInteraction.mode === "move-draft") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    const nextDraft: DraftMeasurementState = {
      ...activeInteraction.initialDraft,
      geometry: translateGeometry(activeInteraction.initialDraft.geometry, dx, dy),
    };
    updateUnsavedDraft(nextDraft);
    return true;
  }

  if (activeInteraction.mode === "vertex") {
    let vertexPoint = pagePoint;
    const activeMeasurement = measurements.find(
      (measurement) => measurement._id === activeInteraction.measurementId
    );
    const vertices = activeInteraction.initialGeometry.vertices;
    const lastIndex = vertices.length - 1;
    const isOpenPath =
      activeMeasurement?.tool === TOOL_IDS.line ||
      activeMeasurement?.tool === TOOL_IDS.polyline;
    const isFirstEndpoint = activeInteraction.vertexIndex === 0 && vertices.length > 1;
    const isLastEndpoint =
      activeInteraction.vertexIndex === lastIndex && activeInteraction.vertexIndex > 0;

    if (isOpenPath && (isFirstEndpoint || isLastEndpoint)) {
      if (isFirstEndpoint) {
        vertexPoint = snapSegmentPoint(vertices[1], pagePoint, {
          allowPdfSnap,
        });
      } else {
        vertexPoint = snapSegmentPoint(vertices[lastIndex - 1], pagePoint, {
          allowPdfSnap,
        });
      }
    } else if (allowPdfSnap) {
      vertexPoint = updateSnapHoverPoint(pagePoint) ?? pagePoint;
    }

    const nextGeometry = mapPolygonPoints(activeInteraction.initialGeometry, (vertex, index) =>
      index === activeInteraction.vertexIndex ? vertexPoint : vertex);

    setPreviewGeometries({ [String(activeInteraction.measurementId)]: nextGeometry });
    return true;
  }

  if (activeInteraction.mode === "resize") {
    if (activeInteraction.initialGeometry.type !== MEASUREMENT_GEOMETRY_TYPES.rect) {
      return true;
    }

    const initialRect = activeInteraction.initialGeometry;
    const right = initialRect.x + initialRect.width;
    const bottom = initialRect.y + initialRect.height;
    let resizedRect: MeasurementGeometry;
    let anchor: Point;
    switch (activeInteraction.handle) {
      case "nw":
        anchor = { x: right, y: bottom };
        resizedRect = normalizeRect(
          anchor,
          snapRectangleCorner(anchor, pagePoint, false, {
            allowPdfSnap,
          })
        );
        break;
      case "ne":
        anchor = { x: initialRect.x, y: bottom };
        resizedRect = normalizeRect(
          anchor,
          snapRectangleCorner(anchor, pagePoint, false, {
            allowPdfSnap,
          })
        );
        break;
      case "sw":
        anchor = { x: right, y: initialRect.y };
        resizedRect = normalizeRect(
          anchor,
          snapRectangleCorner(anchor, pagePoint, false, {
            allowPdfSnap,
          })
        );
        break;
      case "se":
        anchor = { x: initialRect.x, y: initialRect.y };
        resizedRect = normalizeRect(
          anchor,
          snapRectangleCorner(anchor, pagePoint, false, {
            allowPdfSnap,
          })
        );
        break;
    }
    setPreviewGeometries({ [String(activeInteraction.measurementId)]: resizedRect });
    return true;
  }

  if (activeInteraction.mode === "move-label") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    setPreviewAnnotations({
      [String(activeInteraction.measurementId)]: {
        ...activeInteraction.initialAnnotation,
        leaderVisible:
          activeInteraction.initialAnnotation?.leaderVisible ?? (Math.abs(dx) > 1e-6 || Math.abs(dy) > 1e-6),
        textOffset: {
          x: (activeInteraction.initialAnnotation?.textOffset?.x ?? 0) + dx,
          y: (activeInteraction.initialAnnotation?.textOffset?.y ?? 0) + dy,
        },
      },
    });
    return true;
  }

  if (activeInteraction.mode === "resize-label") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    // Keep the label center fixed: each side contributes half the width change.
    const widthDelta = activeInteraction.handle === "e" ? 2 * dx : -2 * dx;
    const minWidth = screenLengthToPageWidth(
      MIN_LABEL_TEXT_BOX_WIDTH_SCREEN_PX,
      (point) => viewport.transforms.pageToScreenPoint(point)
    );
    const nextWidth = Math.max(minWidth, activeInteraction.initialWidth + widthDelta);
    setPreviewAnnotations({
      [String(activeInteraction.measurementId)]: {
        ...activeInteraction.initialAnnotation,
        textBoxWidth: nextWidth,
      },
    });
    return true;
  }

  return false;
}
